<script setup lang="ts">
import { useScrollTo } from '#imports'
const { isMobile } = useBreakpoint()

const {
  altImage = '',
  arrowScrollText = '',
  arrowScrollTo = null,
  darkTheme = false,
  label = '',
  picture = '',
  subtitle = '',
  title = '',
  vAlign = true,
} = defineProps<{
  altImage?: string
  arrowScrollText?: string
  arrowScrollTo?: string | null
  darkTheme?: boolean
  label?: string
  picture?: string
  subtitle?: string
  title?: string
  vAlign?: boolean
}>()
defineSlots<{ default(): any }>()
</script>

<template>
  <section :class="['heading-block__img-wrapper', { 'items-center': vAlign }]">
    <BaseNuxtImg
      v-if="picture"
      :alt="altImage"
      :aspect-ratio="isMobile ? undefined : '16/9'"
      class="heading-block__img"
      loading="eager"
      :src="picture"
    />
    <div v-if="!darkTheme" class="heading-block__overlay" />
    <div :class="['lc-container', { 'mt-28 md:mt-32': !vAlign }]">
      <div class="lc-row justify-center">
        <div class="relative w-full">
          <h1
            v-if="label"
            :class="[
              'mx-auto font-sansSerif text-base font-normal md:w-20/24 lg:w-14/24',
              {
                'text-white': !darkTheme,
                'text-gray-700': darkTheme,
                'mb-10 mt-8 uppercase tracking-widest': !vAlign,
                'my-2': vAlign,
              },
            ]"
            v-html="label"
          />
          <h2
            :class="[
              'heading-block__title mx-auto mb-5 md:w-20/24 lg:w-14/24',
              { 'heading-block__title--white': !darkTheme },
            ]"
            v-html="title || $t('home.headline')"
          />
          <p
            v-if="subtitle"
            :class="[
              'heading-block__subtitle mx-auto whitespace-pre-line md:w-20/24 lg:w-14/24',
              { 'heading-block__subtitle--white': !darkTheme },
            ]"
            v-html="subtitle"
          />
          <slot />
        </div>
      </div>
    </div>

    <div
      v-if="arrowScrollText"
      :class="[
        'absolute bottom-[6.5rem] left-1/2 w-2/3 -translate-x-1/2 md:bottom-[7.5rem]',
        { 'text-white': !darkTheme, 'font-medium text-gray-700': darkTheme },
      ]"
    >
      {{ arrowScrollText }}
    </div>

    <BaseArrowScroll
      v-if="arrowScrollTo"
      :dark-theme="darkTheme"
      class="heading-block__arrow-scroll"
      @click="useScrollTo({ id: arrowScrollTo })"
    />
  </section>
</template>

<style>
.heading-block__img-wrapper {
  min-height: 568px;
  @apply text-center flex relative h-screen w-screen max-w-full;
}

.heading-block__img {
  @apply absolute w-full h-full -z-1;
}

.heading-block__overlay {
  @apply bg-gray-700 bg-opacity-40 absolute w-full h-full;
}

.heading-block__title {
  @apply text-7xl;
}

@screen md {
  .heading-block__title {
    @apply text-8xl;
  }
}

.heading-block__title,
.heading-block__subtitle {
  @apply text-gray-700;
}
.heading-block__title--white,
.heading-block__subtitle--white {
  @apply text-white;
}

.heading-block__subtitle {
  margin-top: 1rem;
  @apply text-base font-normal;
}

@screen md {
  .heading-block__subtitle {
    margin-bottom: 1.5rem;
  }
}

.heading-block__arrow-scroll {
  @apply absolute bottom-0 left-1/2;
  transform: translateX(-50%);
}
</style>
